import { Component, DestroyRef, OnDestroy, OnInit } from '@angular/core';
import { takeUntilDestroyed } from '@angular/core/rxjs-interop';
import { trigger, state, style } from '@angular/animations';
import { moveItemInArray } from '@angular/cdk/drag-drop';
import { PageEvent } from '@angular/material/paginator';
import moment from 'moment';
import { uniqWith, isEqual } from "lodash";
import { Observable, of, Subscription } from 'rxjs';
import { filter, map, mergeMap, switchMap, take, tap } from 'rxjs/operators';
import { ToastrService } from 'ngx-toastr';

import { ViewMode } from '../../core/enums/view-mode.enum';
import { StartOptions } from '../../core/models/enums/start-options.enum';
import { MonitorSortEnum } from '../../core/enums/monitor-sort.enum';
import { MonitorMenuDialogDessagesEnum } from '../../core/enums/toastr-messages/monitor-menu-dialog-messages.enum';
import { MonitorKey } from '../../core/models/view-models/monitor-models/monitor-key.type';
import { BehaviorScreen } from '../../core/models/view-models/monitor-models/screen.type';
import { ResetMonitorImageResolutionViewModel } from '../../core/models/view-models/monitor-models/reset-monitor-image-resolution.view-model';
import { Machine } from '../../core/models/entities/machine.type';
import { MonitorStartOption } from '../../core/models/view-models/monitor-models/monitor-start-option.model';
import { AppConstants } from '../../app.constants';
import { CalendarViewMode } from '../../core/enums/calendar-view-mode.enum';

import { ScreensStateService } from '../../core/services/screen-state/screens-state.service';
import { UIService } from '../../core/services/utils/ui.service';
import { MonitorsService } from '../../core/services/http-services/monitors.service';
import { createSelection } from '../../core/helpers/create-selection.helper';
import { MonitorChangeViewService } from '../../core/services/http-services/monitor-change-view.service';
import { RemoteControlService } from '../../core/services/http-services/remote-control.service';
import { AuthService } from '../../core/services/auth/auth.service';

@Component({
  selector: 'main-content',
  animations: [
    trigger('openClose', [
      state(
        'open',
        style({
          bottom: '5px',
        })
      ),
      state(
        'closed',
        style({
          bottom: '-250px',
        })
      ),
    ]),
  ],
  templateUrl: './main-content.component.html',
  styleUrls: ['./main-content.component.scss'],
})
export class MainContentComponent implements OnDestroy, OnInit {

  screens: BehaviorScreen[] = [];
  locations: Machine[] = [];
  isScreenDataLoaded: boolean;
  skeletonMonitorsArr = new Array(30);
  subscriptions: Subscription = new Subscription();

  selectedItems: MonitorKey[] = [];
  selectedScreens: BehaviorScreen[] = [];
  isSelectingEnabled = false;
  locationFilterExpression: any = null;
  monitorsNameFilterExpression: string = null;
  viewMode = ViewMode.MonitorsSmall;
  viewModes = ViewMode;
  selection: any = null;
  remoteControlMonitorKey: MonitorKey = null;
  remoteControlMonitor: BehaviorScreen = null;

  filteredMonitors$: Observable<BehaviorScreen[]>;
  startOptions: StartOptions[] = [StartOptions.Default, StartOptions.Playlist];
  selectedStartOption: StartOptions = null;
  monitorSortingMode = MonitorSortEnum.SortByMonitorTitle;
  monitorSortingModes = MonitorSortEnum;
  calendarViewMode = CalendarViewMode.Day;
  calendarViewModes = CalendarViewMode;

  date: Date = moment().toDate();
  calendarTitle = 'Today';
  isBottomPanelVisible: boolean;
  isBottomPanelButtonHidden: boolean;
  isRedirecting: boolean;
  administratorRole: string = AppConstants.ADMINISTRATOR_ROLE;
  categoryManagerRole: string = AppConstants.CATEGORY_MANAGER_ROLE;

  selectedPage: number = AppConstants.SELECTED_PAGE_INDEX_DEFAULT_VALUE;
  itemsPerPage = +localStorage.getItem(AppConstants.SCREENS_PER_PAGE) || AppConstants.SCREENS_PER_PAGE_DEFAULT_VALUE;

  constructor(
    public screenStateService: ScreensStateService,
    public uiService: UIService,
    private readonly monitorsService: MonitorsService,
    private readonly monitorChangeViewService: MonitorChangeViewService,
    private readonly remoteControlService: RemoteControlService,
    private readonly authService: AuthService,
    private readonly toastrService: ToastrService,
    private readonly destroyRef: DestroyRef
  ) {
    this.screenStateService.screensCollectionChanged.pipe(
      mergeMap(data => {
        const addedScreens = data?.filter(mon => !this.screens.some(screen =>
          screen.MonitorHandler === mon.MonitorHandler && screen.MachinePhysicalAddress === mon.MachinePhysicalAddress
        ));

        if (data) {
          this.isScreenDataLoaded = true;
          this.screens = [...(data ? data : [])];
        }

        if (addedScreens && addedScreens.length) {
          const machines: Machine[] = uniqWith(addedScreens.map(screen => screen.Machine), isEqual());

          if (machines && machines.length) {
            const imageModel = this.generateResetImageResolutionModel(machines);
            return this.monitorChangeViewService.resetImageResolution(imageModel);
          }
        }
        return of();
      }),
      takeUntilDestroyed()
    ).subscribe();

    this.subscriptions.add(
      this.uiService.bottomPanelVisibleSubject.subscribe((data) => {
        this.isBottomPanelVisible = data;
        setTimeout(() => {
          this.isBottomPanelButtonHidden = this.uiService.isBottomPanelButtonHidden;
        });
      })
    );

    this.subscriptions.add(
      this.monitorsService.getAllMonitors().subscribe()
    );

    this.subscriptions.add(
      this.remoteControlService.remoteControlMonitorKey.subscribe((data: MonitorKey) => {
        this.remoteControlMonitorKey = data;
        this.remoteControlMonitor = this.screens.find((x: BehaviorScreen) =>
          x.MonitorHandler === data?.MonitorHandler && x.MachinePhysicalAddress === data?.MachinePhysicalAddress);
        if (data) {
          this.monitorChangeViewService.monitorsViewChange.next(ViewMode.FullScreen);
        }
      })
    );
  }

  ngOnInit() {
    this.selection = createSelection(this, ['monitor', 'list-item'],
      () => this.screenStateService.changeScreensByIds(this.selectedItems));

    this.subscriptions.add(
      this.screenStateService.locationsChanged.pipe(
        map(data => {
          const addedLocations = this.screenStateService.filterNewLocations(data, this.locations);
          this.locations = [...(data ?? [])];
          return addedLocations;
        }),
        filter(locations => !!locations.length),
        map(locations => locations.map(location => location.PhysicalAddress)),
        switchMap(physicalAddresses => this.monitorChangeViewService.informServerMonitorConnectedToWebMonitorGroup(physicalAddresses, true))
      ).subscribe()
    );

    this.filteredMonitors$ = this.screenStateService.filteredMonitorsInTheDashboard.pipe(
      tap(filteredMonitors => {
        if (filteredMonitors.length === this.screenStateService.screensCollectionChanged.value.length) {
          const maxPageIndex = Math.max(0, Math.ceil(filteredMonitors.length / this.itemsPerPage) - 1);

          if (this.selectedPage > maxPageIndex) {
            this.selectedPage = maxPageIndex;
          }
        }
        else {
          this.selectedPage = AppConstants.SELECTED_PAGE_INDEX_DEFAULT_VALUE;
        }
      })
    );

    this.authService.getCaller()
      .pipe(take(1))
      .subscribe(user =>
        this.remoteControlService.isRemoteControlAllowed.next(user.HasAccessToRemoteControl));

    this.monitorChangeViewService.selectedForFullScreenChanged.pipe(
      map(selectedScreen => selectedScreen || this.monitorChangeViewService.fullScreenMonitor),
      filter(selectedForFullScreenMonitor => !!selectedForFullScreenMonitor),
      takeUntilDestroyed(this.destroyRef)
    ).subscribe(selectedForFullScreenMonitor =>
      this.screenStateService.connectFullScreenMonitorAndDisconnectRest(this.screens, selectedForFullScreenMonitor)
    );

    this.updateStartOption();
  }

  ngOnDestroy() {
    this.screens.forEach(screen => {
      if (screen.isConnected) {
        screen.disconnect();
      }
    });
    this.subscriptions.unsubscribe();
    this.selection.destroy();
  }

  drop(event): void {
    moveItemInArray(this.screens, event.previousIndex, event.currentIndex);
  }

  onCalendarBackward() {
    const unit = this.calendarViewMode as moment.unitOfTime.DurationConstructor;

    this.date = moment(this.date).subtract(1, unit).toDate();
    this.changeTitle();
  }

  onCalendarForwards() {
    const unit = this.calendarViewMode as moment.unitOfTime.DurationConstructor;

    this.date = moment(this.date).add(1, unit).toDate();
    this.changeTitle();
  }

  onSelectAllMonitorsChanged(isChecked: boolean): void {
    isChecked ?
      this.screenStateService.selectAll():
      this.screenStateService.unselectAll();
  }

  onPageChange(event: PageEvent) {
    this.itemsPerPage = event.pageSize;
    this.selectedPage = event.pageIndex;
    localStorage.setItem(AppConstants.SCREENS_PER_PAGE, this.itemsPerPage.toString());
  }

  refreshStartOptions(startOption: StartOptions) {
    const monitorStartOptions: MonitorStartOption[] = [];

    this.selectedScreens.forEach(screen => {
      monitorStartOptions.push(
        new MonitorStartOption(screen.MachinePhysicalAddress, screen.MonitorHandler, startOption)
      );
    });

    this.monitorsService.updateStartOptions(monitorStartOptions).pipe(take(1)).subscribe(() => {
      this.toastrService.success(MonitorMenuDialogDessagesEnum.MonitorsStartOptionUpdatedDescription,
        MonitorMenuDialogDessagesEnum.MonitorUpdateSuccessTitle);
    });
  }

  trackScreen(index, screen: BehaviorScreen) {
    return `${screen.MachinePhysicalAddress}:${screen.MonitorHandler}`;
  }

  public changeTitle() {
    const now = moment().toDate();
    switch (this.calendarViewMode) {
      case this.calendarViewModes.Day:
        switch (this.date.toDateString()) {
          case now.toDateString():
            this.calendarTitle = 'Today';
            break;
          case moment(now).subtract(1, 'days').toDate().toDateString():
            this.calendarTitle = 'Yesterday';
            break;
          case moment(now).add(1, 'days').toDate().toDateString():
            this.calendarTitle = 'Tomorrow';
            break;
          default:
            this.calendarTitle = moment(this.date).format('dddd D/MM/YYYY');
            break;
        }
        break;
      case this.calendarViewModes.Week:
        this.calendarTitle = `${moment(this.date).format('dddd D/MM/YYYY')} - ${moment(this.date).add(1, 'week').format('dddd D/MM/YYYY')}`;
        break;
      case this.calendarViewModes.Month:
        this.calendarTitle = `${moment(this.date).format('dddd D/MM/YYYY')} - ${moment(this.date).add(1, 'month').format('dddd D/MM/YYYY')}`;
        break;
    }
  }

  public originalOrder = (): number => {
    return 0;
  }

  private generateResetImageResolutionModel(machines: Machine[]): ResetMonitorImageResolutionViewModel[] {

    const resetMonitorsViewModel: ResetMonitorImageResolutionViewModel[] = machines.map((machine: Machine) => {
      const monitorForCurrentLocation: BehaviorScreen[] = this.screens.filter((screen: BehaviorScreen) =>
        screen.MachinePhysicalAddress === machine.PhysicalAddress);

      return {
        MachinePhysicalAddress: machine.PhysicalAddress,
        MonitorHandlers: monitorForCurrentLocation.map(screen => screen.MonitorHandler)
      } as ResetMonitorImageResolutionViewModel;
    });

    return resetMonitorsViewModel;
  }

  private updateStartOption(): void {
    this.subscriptions.add(
      this.screenStateService.selectedScreensChanged.subscribe(screens => {

        this.selectedScreens = screens;

        if (screens.length && screens.every(screen => screen.StartOptions === StartOptions.Default)) {
          this.selectedStartOption = StartOptions.Default;
        }
        else if (screens.length && screens.every(screen => screen.StartOptions === StartOptions.Playlist)) {
          this.selectedStartOption = StartOptions.Playlist;
        }
        else {
          this.selectedStartOption = null;
        }
      })
    );
  }
}
